import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { hubUrl } from '../hub';
import { rememberedAccounts } from '../api';
import type { AuthResponse } from '@epos/contracts';
import { TwoFactorGate } from './TwoFactorGate';

export function LoginPage() {
  const { login, loginWithPasskey, loginWithPin, finishAuthentication } = useAuth();
  const navigate = useNavigate();
  const loginParameters = new URLSearchParams(window.location.search);
  const [email, setEmail] = useState(loginParameters.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [selectedHandle, setSelectedHandle] = useState('');
  const [pin, setPin] = useState('');
  const [twoFactor, setTwoFactor] = useState<AuthResponse>();
  const savedAccountSubmitted = useRef(false);
  // The preload bridge exists only inside the trusted EPOS desktop window.
  // Detect it directly so credentials are restored even when an expired web
  // session redirects to /login without preserving the desktop query string.
  const desktop = Boolean(window.eposDesktop);
  const kioskUnlock = loginParameters.get('kioskUnlock') === '1' && desktop;
  const accountFlow = loginParameters.get('accountFlow') === '1' && desktop;
  const newAccount = loginParameters.get('newAccount') === '1' && accountFlow;
  const requestedStart = loginParameters.get('start');
  const startPath = desktop && ['/pos', '/kiosk', '/kds'].includes(requestedStart ?? '') ? requestedStart! : '/';
  // A venue desktop is expected to remember its operator credentials. The
  // operator may opt out, but first-time desktop sign-in now saves by default.
  const [saveCredentials, setSaveCredentials] = useState(desktop);
  const [loadingSavedAccount, setLoadingSavedAccount] = useState(accountFlow && !newAccount);
  const accounts = rememberedAccounts.get().filter(account => new Date(account.expiresAt) > new Date());
  const finishSignIn = useCallback(async () => {
    if (kioskUnlock) {
      await window.eposDesktop!.kiosk.completeUnlock();
      navigate('/device-settings?kioskReturn=1', { replace: true });
    } else navigate(startPath, { replace: true });
  }, [kioskUnlock, navigate, startPath]);
  const authenticatePassword = useCallback(async (loginEmail: string, loginPassword: string, remember: boolean) => {
    setPending(true);
    setError('');
    try {
      // A saved desktop account still supplies its Windows-encrypted password,
      // but an explicitly trusted venue device may use the server's normal
      // trusted-device lifetime instead of forcing MFA on every module launch.
      const response = await login({ email: loginEmail, password: loginPassword });
      if (desktop) {
        if (remember) await window.eposDesktop!.credentials.save({ email: loginEmail.trim(), password: loginPassword });
        else await window.eposDesktop!.credentials.clear();
      }
      if ((response.authenticationStage ?? 'complete') === 'complete') await finishSignIn();
      else setTwoFactor(response);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to sign in');
    } finally {
      setPending(false);
    }
  }, [desktop, finishSignIn, login]);
  useEffect(() => {
    if (!desktop) return;
    // Adding a connector user must always begin with a blank login form. The
    // connector keeps the currently allocated user until this new login has
    // actually succeeded, so cancelling cannot silently break device routing.
    if (newAccount) {
      setLoadingSavedAccount(false);
      return;
    }
    let active = true;
    window.eposDesktop?.credentials.load().then(async saved => {
      if (!active) return;
      if (!saved) {
        setLoadingSavedAccount(false);
        return;
      }
      setEmail(saved.email);
      setPassword(saved.password);
      setSaveCredentials(true);
      // Do not wait for React state and a rendered form to agree before using
      // the saved account. Call authentication with the decrypted values
      // directly so the only visible challenge is MFA.
      if (accountFlow && saved.password && !savedAccountSubmitted.current) {
        savedAccountSubmitted.current = true;
        await authenticatePassword(saved.email, saved.password, true);
      }
      if (active) setLoadingSavedAccount(false);
    }).catch(() => { if (active) setLoadingSavedAccount(false); });
    return () => { active = false; };
  }, [accountFlow, authenticatePassword, desktop, newAccount]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await authenticatePassword(email, password, accountFlow || saveCredentials);
  };
  const passkey = async () => { if (!email) { setError('Enter your email address first.'); return; } setPending(true); setError(''); try { await loginWithPasskey(email); await finishSignIn(); } catch (caught) { setError(caught instanceof Error ? caught.message : 'Unable to sign in with a passkey'); } finally { setPending(false); } };
  const quickPin = async (event: FormEvent) => { event.preventDefault(); setPending(true); setError(''); try { await loginWithPin(selectedHandle, pin); await finishSignIn(); } catch (caught) { setError(caught instanceof Error ? caught.message : 'Unable to unlock this account'); } finally { setPending(false); } };
  const completeTwoFactor=async(response:AuthResponse)=>{finishAuthentication(response);try{await finishSignIn();}catch(caught){setError(caught instanceof Error?caught.message:'Unable to open kiosk device settings');}};

  return <main className="legacy-login-page">
    <header className="legacy-public-header"><a href={hubUrl} title="Back to EPOS Hub"><img src="/logo-square-dark.png" alt="EPOS Bookings" /></a><nav><a className="legacy-hub-link" href={hubUrl}>Back to EPOS Hub</a><Link className="legacy-login-nav-button" to="/login">Login</Link></nav></header>
    <div className="legacy-login-container">
      <section className="legacy-login-left"><img src="/logo-light.svg" alt="logo" /></section>
      <section className="legacy-login-right">
        <h1>LogIn</h1>
        {twoFactor ? <TwoFactorGate response={twoFactor} complete={completeTwoFactor} cancel={()=>{setTwoFactor(undefined);setPassword('');}} /> : <>
        {kioskUnlock && <p className="desktop-kiosk-unlock-message"><strong>Staff kiosk unlock</strong><span>Sign in or use your personal quick PIN to open device settings.</span></p>}
        {!accountFlow && accounts.length > 0 && <div className="remembered-account-panel"><h2>Who is using EPOS Bookings?</h2><div className="remembered-account-list">{accounts.map(account => <button type="button" key={account.handle} className={selectedHandle===account.handle?'selected':''} onClick={()=>{setSelectedHandle(account.handle);setEmail(account.email)}}><strong>{account.name} {account.surname}</strong><small>{account.email}</small></button>)}</div>{selectedHandle && <form onSubmit={quickPin}><div className="legacy-floating-field"><input inputMode="numeric" autoComplete="off" required minLength={6} maxLength={12} value={pin} onChange={event=>setPin(event.target.value.replace(/\D/g,''))} placeholder="Personal quick PIN"/><label>Personal quick PIN</label></div><input className="legacy-submit" type="submit" value={pending?'Unlocking…':'Unlock account'} disabled={pending}/></form>}<p className="legacy-forgot"><button type="button" onClick={()=>setSelectedHandle('')}>Sign in another user</button></p></div>}
        {loadingSavedAccount ? <div className="remembered-account-panel" role="status"><h2>Verifying saved account…</h2><p>Your encrypted username and password are being checked. Two-factor authentication will appear next.</p></div> : <form onSubmit={submit}>
          <div className="legacy-floating-field"><input id="email" placeholder="Enter Email Address" type="email" autoComplete="username" required value={email} onChange={event => setEmail(event.target.value)} /><label htmlFor="email">Email<span>*</span></label></div>
          <div className="legacy-floating-field"><input id="password" placeholder="Enter Password" type="password" autoComplete="current-password" required value={password} onChange={event => setPassword(event.target.value)} /><label htmlFor="password">Password<span>*</span></label></div>
          {desktop && <label className="desktop-remember-credentials"><input type="checkbox" checked={accountFlow || saveCredentials} disabled={accountFlow} onChange={event=>setSaveCredentials(event.target.checked)}/><span><strong>{accountFlow ? 'Save this user to the Device Connector' : 'Save username and password on this device'}</strong><small>{accountFlow ? 'Required so this connector can retain its selected operator and route device work correctly.' : "Stored using Windows encryption. Two-factor authentication follows this account's trusted-device policy."}</small></span></label>}
          {error && <p className="error">{error}</p>}
          <input className="legacy-submit" type="submit" value={pending ? 'Signing in…' : 'Submit'} disabled={pending} />
          <button className="legacy-submit" type="button" disabled={pending} onClick={passkey}>Use phone passkey or security key</button>
          <div className="legacy-forgot"><Link to="/forgot-password">Forgot Your Password?</Link></div>
        </form>}
        </>}
      </section>
    </div>
  </main>;
}
